import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { QuantityStepper, money } from './QuantityStepper';
import { ArrowRightIcon, CheckIcon, ChevronDownIcon, BuildingIcon } from 'lucide-react';
import { UspsBadge } from './UspsBadge';
import { SecureCheckoutNote } from './SecureCheckoutNote';
import { GoldMedal } from './LifetimeSeal';
import { PrescriptionBadge } from './PrescriptionBadge';
import { UpsellModal } from './UpsellModal';
import { companyPack } from '../data/product';
import { cartLink, variantIdOf, PRODUCT_LINKS, ProductLinkKey } from '../data/productLinks';
import { buildFlow, checkoutFlow, packTotal, BUSINESS_PRICE } from '../data/upsellFlows';
import { useActiveSale } from '../hooks/useActiveSale';
import { useTrackOnView } from '../hooks/useTrackOnView';
import { useUpsellFlow } from '../hooks/useUpsellFlow';
import { flowSeen, setSelectionSource } from '../utils/funnelState';
import { onBusinessQuantityRequest } from '../utils/offerBus';
import { trackCustom, packEvents, trackShopifyAddToCart } from '../utils/tracking';
import { setPendingCare, setPendingRx } from '../utils/cartAddons';
import { RxQuantityControl } from './RxQuantityControl';
import { CareKitControl } from './CareKitControl';
export function CompanyPack() {
  const [quantity, setQuantity] = useState(1);
  const [missingLink, setMissingLink] = useState(false);
  const [rxQty, setRxQty] = useState(0);
  const [careQty, setCareQty] = useState(0);
  // Reached by a quantity switch from a smaller card: skip the business ladder.
  const [reachedBySwitch, setReachedBySwitch] = useState(false);
  const viewRef = useTrackOnView<HTMLElement>('business_pack_viewed');
  const sale = useActiveSale();
  const upsell = useUpsellFlow();
  useEffect(() => onBusinessQuantityRequest(next => {
    setQuantity(Math.max(1, Math.min(10, next)));
    setReachedBySwitch(true);
    setMissingLink(false);
  }), []);

  // Two 30-packs are recognized as one 60-system deployment at the approved price.
  const isSixty = quantity === 2;
  const {
    units,
    total
  } = packTotal('business', quantity);
  const compare = companyPack.compareValue * quantity;
  const linkKey: ProductLinkKey = isSixty ? 'sixtyX1BusinessPrivate129990' : companyPack.link;
  const href = cartLink(linkKey, isSixty ? 1 : quantity);
  const handleQuantity = (next: number) => {
    if (next === quantity) return;
    trackCustom(next > quantity ? 'quantity_increased' : 'quantity_decreased', {
      pack: 'business',
      from: quantity,
      to: next
    });
    if (next === 2) trackCustom('pack_switch_suggested', {
      from: 'business',
      to: 'sixty',
      quantity: next
    });
    setMissingLink(false);
    setReachedBySwitch(false);
    setQuantity(next);
  };
  return <section ref={viewRef} id="company" aria-labelledby="company-pack-heading" className="mt-10 scroll-mt-28 border-2 border-grit-500 bg-ink-950 lg:mt-12" data-id="element-395" data-mp="1w877yr2ictub">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-grit-500/40 bg-grit-500 px-5 py-3 text-ink-950 sm:px-8" data-id="element-396" data-mp="37n43mjt76han">
        <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em]" data-id="element-397" data-mp="3g791p3r4tmx0">
          <BuildingIcon className="h-4 w-4" aria-hidden="true" data-id="element-398" data-mp="3k4na4t6znqny" />
          {companyPack.topBadge}
        </span>
        <span className="border border-ink-950 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em]" data-id="element-399" data-mp="3b61tj3l2ulpt">
          {companyPack.secondaryBadge}
        </span>
      </div>

      {/* The artwork carries the offer, unit count, price and per-X1 cost; the copy below adds what it doesn't. */}
      <h3 id="company-pack-heading" className="sr-only" data-id="element-400" data-mp="7r5s2sgglo61">
        {companyPack.title}: Buy 20, get 10 free. {companyPack.units}.
      </h3>
      {companyPack.image ? <img src={companyPack.image} alt={companyPack.imageAlt} loading="lazy" width={1536} height={1024} className="block h-auto w-full border-b border-grit-500/40" data-id="element-401" data-mp="3u3co4avrkqyx" /> : <div className="border-b border-grit-500/40 px-5 pb-6 pt-8 sm:px-8 lg:px-12 lg:pt-10" data-id="element-402" data-mp="2xz5pd4i892xp">
          <p className="grit-headline text-[clamp(2.5rem,7vw,4.5rem)] font-extrabold uppercase leading-[0.92] text-grit-500" data-id="element-403" data-mp="2ty25m6x26mr7">
            {companyPack.offer}
          </p>
          <p className="mt-3 font-display text-2xl font-bold uppercase tracking-wide text-offwhite sm:text-3xl" data-id="element-404" data-mp="2p0tp0ark6qs2">
            {companyPack.units} · {companyPack.perUnit}
          </p>
        </div>}

      <div className="flex flex-col gap-8 p-5 sm:p-8 lg:grid lg:grid-cols-[1.15fr_0.85fr] lg:gap-14 lg:p-12" data-id="element-405" data-mp="3hp7n0qindn3m">
        <div data-id="element-406" data-mp="37xldcylyjw2z">
          <p className="border-l-4 border-grit-500 pl-4 font-display text-2xl font-bold uppercase leading-tight text-offwhite sm:text-3xl" data-id="element-407" data-mp="37x1vtj07z9ph">
            {companyPack.headline}
          </p>
          <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-steel-300 sm:text-base" data-id="element-408" data-mp="488b2639ua9c">{companyPack.copy}</p>

          <details className="group mt-8 border-t border-white/10 pt-6" data-id="element-409" data-mp="3vmnxcd42e10n">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-[11px] font-bold uppercase tracking-[0.2em] text-steel-300 transition-colors duration-150 ease-grit hover:text-offwhite [&::-webkit-details-marker]:hidden" data-id="element-410" data-mp="6sxyw013iz6k">
              Full list of what ships in the crew pack
              <ChevronDownIcon className="h-4 w-4 shrink-0 transition-transform duration-150 ease-grit group-open:rotate-180" aria-hidden="true" data-id="element-411" data-mp="lqqbajbcj7gj" />
            </summary>
            <ul className="mt-4 grid gap-2.5 lg:grid-cols-2" data-id="element-412" data-mp="uq3lje7ao02b">
              {companyPack.includes.map(item => <li key={item} className="flex items-start gap-2.5 text-[14px] font-medium text-steel-200" data-id="element-413" data-mp="ziwru6ievggi">
                  <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-414" data-mp="14g5bycn4duke" />
                  {item}
                </li>)}
            </ul>
          </details>
        </div>

        <div className="-order-1 flex flex-col justify-center border border-white/12 bg-ink-900 p-5 sm:p-6 lg:order-none lg:p-8" data-id="element-415" data-mp="1dhmmzglo7628">
          <p className="mb-4 flex items-center justify-between gap-3 bg-grit-500 px-4 py-2.5 text-ink-950" data-id="element-416" data-mp="35iwhe3xvtv4a">
            <span className="font-display text-2xl font-extrabold uppercase leading-none" data-id="element-417" data-mp="3ja2n4hrr8ozm">
              Save {money(companyPack.saveValue)}
            </span>
            <span className="text-right text-[10px] font-extrabold uppercase leading-tight tracking-[0.16em]" data-id="element-418" data-mp="3eib4quir5pkk">
              {sale.name}
              <span className="block" data-id="element-419" data-mp="3o9nu9fh2w3nf">+ 10 free units</span>
            </span>
          </p>
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-steel-400" data-id="element-420" data-mp="63styl1ffvx9">Your price</p>
          <p className="text-[15px] text-steel-400 line-through" data-id="element-421" data-mp="22gy3xrymegm">{money(compare)}</p>
          <p className="font-display text-[clamp(3.5rem,13vw,7rem)] font-extrabold leading-none text-offwhite" data-id="element-422" data-mp="20csnbeex7e8">
            {money(total)}
          </p>
          <p className="mt-4 font-display text-xl font-bold uppercase tracking-wide text-grit-500" data-id="element-423" data-mp="23uvo7k4nqp84">
            You save {money(compare - total)}
          </p>
          <p className="mt-2 text-[15px] text-steel-300" data-id="element-424" data-mp="1xqz5hubr4bqg">
            {quantity > 1 ? `${quantity} packs · ${units} complete systems total.` : `${companyPack.comparison}. Business / crew price: ${companyPack.price}.`}
          </p>
          {isSixty && <p role="status" className="mt-3 border border-grit-500/70 bg-grit-500/10 px-3.5 py-3 text-[13px] font-semibold leading-snug text-steel-200" data-id="element-425" data-mp="1a1moi8a6mzdl">
              Two 30-packs would be {money(BUSINESS_PRICE * 2)}. As one 60-system deployment it&apos;s{' '}
              {money(total)}, about $21.67 per X1. You save {money(BUSINESS_PRICE * 2 - total)} versus two standard
              packs.
            </p>}
          <p className="mt-3 border-t border-white/10 pt-3 text-[13px] leading-relaxed text-steel-400" data-id="element-426" data-mp="2vefx8ydddruo">
            Business volume pricing: 20 systems paid for, 10 bonus systems included. {companyPack.perUnit}, one order
            for the whole crew. Savings compared with 30 single Complete Systems at $49.95.
          </p>

          <RxQuantityControl value={rxQty} max={units} onChange={next => {
          trackCustom('multipack_rx_quantity', {
            pack: 'business',
            quantity: next
          });
          setRxQty(next);
        }} className="mt-6" data-id="element-427" data-mp="7vbyb3aeezt3" />
          <CareKitControl value={careQty} max={units} onChange={next => {
          trackCustom(next > careQty ? 'care_kit_added' : 'care_kit_removed', {
            pack: 'business',
            quantity: next
          });
          setCareQty(next);
        }} className="mt-3" data-id="element-428" data-mp="3q91ixpz925yr" />
          <QuantityStepper value={quantity} onChange={handleQuantity} max={10} label="Company crew pack quantity" className="mt-4" data-id="element-429" data-mp="3c9tjqx336xuk" />
          <a href={href || '#company'} data-buy-intent="true" onClick={event => {
          setPendingRx(Math.min(rxQty, units));
          setPendingCare(Math.min(careQty, units));
          trackCustom('business_pack_clicked', {
            quantity
          });
          trackCustom(packEvents.business.addToCart, {
            quantity,
            value: total
          });
          trackCustom('product_selected', {
            pack: 'business',
            quantity
          });
          const next = checkoutFlow('business', quantity, {
            reachedBySwitch
          });
          if (next && !flowSeen(next.kind)) {
            event.preventDefault();
            upsell.start(buildFlow(next.kind, next.source));
            return;
          }
          if (!href) {
            event.preventDefault();
            setMissingLink(true);
            console.warn(`[GritVue] PRODUCT_LINKS.${linkKey} is empty.`);
            return;
          }
          setSelectionSource(isSixty ? 'business_expansion' : reachedBySwitch ? 'manual_quantity_thirty' : 'direct_thirty_pack');
          trackCustom('upsell_flow_completed', {
            final_product: linkKey,
            value: total,
            quantity,
            result: 'direct'
          });
          trackShopifyAddToCart(event, {
            content_name: isSixty ? 'GritVue X1 · 60 complete systems · business deployment' : companyPack.title,
            content_ids: [variantIdOf(PRODUCT_LINKS[linkKey])],
            quantity: isSixty ? 1 : quantity,
            value: Number(total.toFixed(2))
          });
        }} className="group mt-5 flex items-center justify-center gap-3 bg-grit-500 px-5 py-4 text-center font-display text-lg font-extrabold uppercase leading-tight tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite sm:px-6 sm:py-5 sm:text-2xl" data-id="element-430" data-mp="1ahnhfxo1vct1">
            {isSixty ? `Equip 60 for ${money(total)}` : quantity > 1 ? `Outfit your crew — get ${units} for ${money(total)}` : companyPack.cta}
            <ArrowRightIcon className="h-5 w-5 shrink-0 transition-transform duration-150 ease-grit group-hover:translate-x-1 sm:h-6 sm:w-6" aria-hidden="true" data-id="element-431" data-mp="1mkulfqtcj33o" />
          </a>
          {missingLink && <p role="alert" className="mt-3 border border-white/20 bg-white/[0.04] px-3 py-2.5 text-[13px] text-steel-200" data-id="element-432" data-mp="17udclmedtawr">
              This checkout link isn&apos;t connected yet. Please try again shortly or email support@gritvuegear.com.
            </p>}
          <p className="mt-3 text-center text-[13px] text-steel-400" data-id="element-433" data-mp="yv07f5xaj8hu">{companyPack.ctaNote}</p>

          <div className="mt-6 grid gap-3 border-t border-white/10 pt-5" data-id="element-434" data-mp="twfapgh8z6y5">
            <UspsBadge note="Free U.S. shipping." data-id="element-435" data-mp="2t6f1fyxc3u3e" />
            <Link to="/legal/guarantee" className="group flex items-center gap-4 border gold-edge bg-[#1a1408] p-4 transition-colors duration-150 ease-grit hover:bg-[#241b0b] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f2c94c]" data-id="element-436" data-mp="2o7k9o7he2v75">
              <GoldMedal size={92} data-id="element-437" data-mp="2ycadyk09mbej" />
              <p className="font-display text-lg font-bold uppercase leading-tight tracking-wide text-[#ffe79a]" data-id="element-438" data-mp="2ydbqkswxj5yd">
                All {units} covered for life.
                <span className="mt-0.5 block text-[13px] font-semibold normal-case tracking-normal text-steel-300" data-id="element-439" data-mp="3r0z7nxyzreg9">
                  Lifetime Replacement Warranty on every system. Break it. We replace it. Terms apply.
                </span>
              </p>
            </Link>
            <PrescriptionBadge compact data-id="element-440" data-mp="3h4w5wt81xhtu" />
            <SecureCheckoutNote className="mt-1" data-id="element-441" data-mp="3m0to752vky99" />
          </div>
        </div>
      </div>

      <UpsellModal step={upsell.step} confirmation={upsell.confirmation} onContinue={upsell.continueToCheckout} onFollowUp={upsell.followUp} routing={upsell.routing} missingLink={upsell.missingLink} onAccept={upsell.accept} onDecline={upsell.decline} onClose={upsell.close} data-id="element-442" data-mp="25mtonv7xzamj" />
    </section>;
}